import React from 'react';
import clsx from 'clsx';
import { useTheme, useMediaQuery } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import DashboardIcon from '@material-ui/icons/Dashboard';
import BusinessCenterIcon from '@material-ui/icons/BusinessCenter';
import CategoryIcon from '@material-ui/icons/Category';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import SettingsIcon from '@material-ui/icons/Settings';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import LocalMallIcon from '@material-ui/icons/LocalMall';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import CancelIcon from '@material-ui/icons/Cancel';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import ConfirmationNumberIcon from '@material-ui/icons/ConfirmationNumber';
import SettingsApplicationsIcon from '@material-ui/icons/SettingsApplications';
import ArtTrackIcon from '@material-ui/icons/ArtTrack';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import PeopleIcon from '@material-ui/icons/People';
import StoreIcon from '@material-ui/icons/Store';
import PersonIcon from '@material-ui/icons/Person';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import RemoveShoppingCartIcon from '@material-ui/icons/RemoveShoppingCart';
import PaymentIcon from '@material-ui/icons/Payment';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Drawer, List, Divider, IconButton, ListSubheader } from '@material-ui/core';

import { handleDrawer } from '../../store/actions/menu';
import { useStyles } from './Style';
import ListItem from './ListItem';
import ListCollapse from './ListCollapse';

const Menu = props => {
    const { classes, user, handleDrawer, drawerOpen } = props;
    const { perfis } = user;
    
    // Responsividade
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const drawerVariant = isMobile ? 'temporary' : 'permanent';
    
    // Rastrear se o drawer foi inicializado
    const initRef = React.useRef(false);
    
    // Inicializar estado do drawer baseado no tamanho da tela (apenas uma vez)
    React.useEffect(() => {
        if (!initRef.current) {
            initRef.current = true;
            
            // Detectar tamanho da tela diretamente
            const isScreenSmall = window.innerWidth < theme.breakpoints.values.sm;
            
            // Se é mobile, deve estar fechado (drawerOpen deve ser false)
            if (isScreenSmall && drawerOpen) {
                handleDrawer(); // Fechar
            }
            // Se é desktop, deve estar aberto (drawerOpen deve ser true)
            else if (!isScreenSmall && !drawerOpen) {
                handleDrawer(); // Abrir
            }
        }
    }, []); // Executa apenas na montagem
    
    // Estilos para o subheader do menu
    const menuSubheaderStyle = {
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
        paddingLeft: theme.spacing(2),
    };
    
    // Extrair apenas as classes necessárias para ListItem
    const listItemClasses = {
        activeItem: classes.activeItem,
        inactiveItem: classes.inactiveItem,
    };

    return (
        <Drawer
            variant={drawerVariant}
            onClose={isMobile ? handleDrawer : undefined}
            classes={{
                paper: clsx(classes.drawerPaper, !drawerOpen && classes.drawerPaperClose),
            }}
            open={drawerOpen}
        >
            <div className={classes.toolbarIcon}>
                <IconButton 
                    onClick={handleDrawer}
                    aria-label={drawerOpen ? "Fechar menu" : "Abrir menu"}
                    aria-expanded={drawerOpen}
                >
                    <ChevronLeftIcon />
                </IconButton>
            </div>
            
            <Divider />
            
            {/* SEÇÃO: PRINCIPAL */}
            <List subheader={<ListSubheader style={menuSubheaderStyle}>Principal</ListSubheader>}>
                <ListItem 
                    titlePrimary='Dashboard' 
                    to={'/dashboard'} 
                    leftIcon={<DashboardIcon />}
                    classes={listItemClasses}
                />
            </List>
            
            <Divider />
            
            {/* SEÇÃO: GERENCIAMENTO (Gerentes) */}
            {(perfis.includes('ADMIN_APP') || perfis.includes('GERENTE_NEGOCIO')) && (
                <>
                    <List subheader={<ListSubheader style={menuSubheaderStyle}>Gerenciamento</ListSubheader>}>
                        <ListItem 
                            titlePrimary='Meu Negócio' 
                            to={'/meu-negocio'} 
                            leftIcon={<BusinessCenterIcon />}
                            classes={listItemClasses}
                        />
                        <ListItem 
                            titlePrimary='Categorias' 
                            to={'/categorias'} 
                            leftIcon={<CategoryIcon />}
                            classes={listItemClasses}
                        />
                        <ListItem 
                            titlePrimary='Produtos' 
                            to={'/produtos'} 
                            leftIcon={<LocalMallIcon />}
                            classes={listItemClasses}
                        />
                        <ListItem 
                            titlePrimary='Funcionários' 
                            to={'/funcionarios-da-loja'} 
                            leftIcon={<AssignmentIndIcon />}
                            classes={listItemClasses}
                        />
                    </List>
                    
                    <Divider />
                    
                    {/* SUBSEÇÃO: VENDAS */}
                    <List subheader={<ListSubheader style={menuSubheaderStyle}>Vendas</ListSubheader>}>
                        <ListItem 
                            titlePrimary='Em Andamento' 
                            to={'/vendas-em-andamento'} 
                            leftIcon={<ShoppingCartIcon />}
                            classes={listItemClasses}
                        />
                        <ListItem 
                            titlePrimary='Entregues' 
                            to={'/vendas-entregues'} 
                            leftIcon={<DoneAllIcon />}
                            classes={listItemClasses}
                        />
                        <ListItem 
                            titlePrimary='Canceladas' 
                            to={'/vendas-canceladas'} 
                            leftIcon={<CancelIcon />}
                            classes={listItemClasses}
                        />
                    </List>
                    
                    <Divider />
                    
                    {/* SUBSEÇÃO: CONFIGURAÇÕES */}
                    <List subheader={<ListSubheader style={menuSubheaderStyle}>Configurações</ListSubheader>}>
                        <ListItem 
                            titlePrimary='Integração Stripe' 
                            to={'/re-stripe-onboarding'} 
                            leftIcon={<CreditCardIcon />}
                            classes={listItemClasses}
                        />
                        <ListItem 
                            titlePrimary='Cupons de Desconto' 
                            to={'/cupom-de-desconto'} 
                            leftIcon={<ConfirmationNumberIcon />}
                            classes={listItemClasses}
                        />
                        <ListItem 
                            titlePrimary='Perguntas e Respostas' 
                            to={'/perguntas-e-respostas'} 
                            leftIcon={<HelpOutlineIcon />}
                            classes={listItemClasses}
                        />
                        <ListItem 
                            titlePrimary='Configurações' 
                            to={'/configuracoes'} 
                            leftIcon={<SettingsIcon />}
                            classes={listItemClasses}
                        />
                    </List>
                </>
            )}
            
            <Divider />
            
            {/* SEÇÃO: ADMINISTRAÇÃO (Admin Only) */}
            {perfis.includes('ADMIN_APP') && (
                <>
                    <List subheader={<ListSubheader style={menuSubheaderStyle}>Administração</ListSubheader>}>
                        <ListCollapse 
                            id='admin' 
                            titlePrimary='Sistema' 
                            leftIcon={<SettingsApplicationsIcon />}
                            classes={listItemClasses}
                        >
                            <ListItem 
                                titleSecondary='Lojas' 
                                to={'/lojas'} 
                                leftIcon={<StoreIcon color='disabled' />}
                                classes={listItemClasses}
                            />
                            <ListItem 
                                titleSecondary='Funcionários' 
                                to={'/funcionarios'} 
                                leftIcon={<PeopleIcon color='disabled'/>}
                                classes={listItemClasses}
                            />
                            <ListItem 
                                titleSecondary='Usuários' 
                                to={'/usuarios'} 
                                leftIcon={<PersonIcon color='disabled'/>}
                                classes={listItemClasses}
                            />
                            <ListItem 
                                titleSecondary='Vendas' 
                                to={'/vendas'} 
                                leftIcon={<ShoppingCartIcon color='disabled'/>}
                                classes={listItemClasses}
                            />
                            <ListItem 
                                titleSecondary='Carrinhos Abandonados' 
                                to={'/carrinhos-abandonados'} 
                                leftIcon={<RemoveShoppingCartIcon color='disabled'/>}
                                classes={listItemClasses}
                            />
                            <ListItem 
                                titleSecondary='Melhor Envio' 
                                to={'/melhor-envio'} 
                                leftIcon={<LocalShippingIcon color='disabled'/>}
                                classes={listItemClasses}
                            />
                             <ListItem 
                                titleSecondary='Repasses' 
                                to={'/repasses-ao-vendedor'} 
                                leftIcon={<PaymentIcon color='disabled'/>}
                                classes={listItemClasses}
                            />
                        </ListCollapse>
                        
                        <ListCollapse 
                            id='publicidade' 
                            titlePrimary='Publicidade' 
                            leftIcon={<ArtTrackIcon />}
                            classes={listItemClasses}
                        >
                            <ListItem 
                                titleSecondary='Listar Campanhas' 
                                to={'/campanhas-publicitarias-no-app'} 
                                leftIcon={<ShoppingCartIcon color='disabled'/>}
                                classes={listItemClasses}
                            />
                            <ListItem 
                                titleSecondary='Nova Campanha' 
                                to={'/campanhas-publicitarias-no-app/cadastrar-campanha'} 
                                leftIcon={<SettingsIcon color='disabled'/>}
                                classes={listItemClasses}
                            />
                            <ListItem 
                                titleSecondary='Notificações Push' 
                                to={'/notificacoes-push'} 
                                leftIcon={<NotificationsActiveIcon color='disabled'/>}
                                classes={listItemClasses}
                            />
                        </ListCollapse>
                        
                        <ListCollapse 
                            id='aplicativo' 
                            titlePrimary='Aplicativo' 
                            leftIcon={<PhoneAndroidIcon />}
                            classes={listItemClasses}
                        >
                            <ListItem 
                                titleSecondary='Versões' 
                                to={'/versoes-app'} 
                                leftIcon={<SettingsIcon color='disabled'/>}
                                classes={listItemClasses}
                            />
                        </ListCollapse>
                    </List>
                </>
            )}
        </Drawer>
    )
}

const mapStateToProps = state => ({
    drawerOpen: state.menu.drawerOpen,
    user: state.auth.user
})
const mapDispatchToProps = dispatch => bindActionCreators({ handleDrawer }, dispatch)

export default withStyles(useStyles)(connect(mapStateToProps, mapDispatchToProps)(Menu))